// Create Note Modal Component
import { useState, useCallback } from 'react';
import { Modal, Input, Form, message } from 'antd';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import './Modals.css';

export default function CreateNoteModal({ isOpen, onClose, onNoteCreated }) {
    const { updateNote, syncNotes } = useNotes();
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleSubmit = useCallback(async () => {
        if (!user) {
            message.error('You must be logged in to create notes');
            return;
        }

        try {
            const values = await form.validateFields();
            setLoading(true);

            // Normalize URL - ensure it has a protocol
            let url = values.url.trim();
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
            }

            // Validate URL format
            try {
                new URL(url);
            } catch {
                message.error('Please enter a valid URL');
                setLoading(false);
                return;
            }

            // Create initial content with title as heading
            const initialContent = JSON.stringify({
                version: 2,
                content: {
                    type: 'doc',
                    content: [
                        {
                            type: 'heading',
                            attrs: { level: 1 },
                            content: values.title ? [{ type: 'text', text: values.title }] : undefined
                        },
                        {
                            type: 'paragraph',
                            content: values.content ? [{ type: 'text', text: values.content }] : undefined
                        }
                    ]
                },
                updatedAt: new Date().toISOString()
            });

            // Save the note using the URL as the key
            await updateNote(url, initialContent);
            
            message.success('Note created successfully');
            form.resetFields();
            onClose();
            
            // Sync notes and notify parent
            await syncNotes();
            onNoteCreated?.(url);
        } catch (error) {
            console.error('Failed to create note:', error);
            if (error.errorFields) {
                // Form validation error - already shown by antd
                return;
            }
            message.error('Failed to create note');
        } finally {
            setLoading(false);
        }
    }, [user, form, updateNote, syncNotes, onClose, onNoteCreated]);

    const handleCancel = useCallback(() => {
        form.resetFields();
        onClose();
    }, [form, onClose]);

    return (
        <Modal
            title="Create New Note"
            open={isOpen}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="Create Note"
            cancelText="Cancel"
            confirmLoading={loading}
            destroyOnClose
            className="create-note-modal"
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
            >
                <Form.Item
                    name="url"
                    label="Website URL"
                    rules={[
                        { required: true, message: 'Please enter a URL' },
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                let url = value.trim();
                                if (!url.startsWith('http://') && !url.startsWith('https://')) {
                                    url = 'https://' + url;
                                }
                                try {
                                    new URL(url);
                                    return Promise.resolve();
                                } catch {
                                    return Promise.reject('Please enter a valid URL');
                                }
                            }
                        }
                    ]}
                >
                    <Input
                        placeholder="https://example.com/page"
                        prefix={<span style={{ color: 'var(--color-text-tertiary)' }}>🔗</span>}
                    />
                </Form.Item>

                <Form.Item
                    name="title"
                    label="Title (optional)"
                >
                    <Input placeholder="My Note Title" />
                </Form.Item>

                <Form.Item
                    name="content"
                    label="Initial Content (optional)"
                >
                    <Input.TextArea
                        placeholder="Start writing your note..."
                        rows={4}
                    />
                </Form.Item>
            </Form>

            <div className="create-note-hint">
                <p>💡 The note will be associated with this URL, just like notes created from the browser extension.</p>
            </div>
        </Modal>
    );
}
